/**
 * Non-destructive admin impersonation of agencies.
 *
 * Stored in sessionStorage so it clears on tab close and never touches the DB.
 * Used by `useAgencyRole` to redirect agency-scoped queries to the impersonated
 * agency when the current user is a platform admin.
 */

const STORAGE_KEY = "admin_impersonated_agency";

export interface ImpersonatedAgency {
  id: string;
  name: string;
}

export function setImpersonatedAgency(id: string, name: string) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, name }));
    // Notify same-tab listeners (the storage event only fires across tabs)
    window.dispatchEvent(new Event("admin-impersonation-change"));
  } catch (err) {
    console.error("Failed to set impersonated agency:", err);
  }
}

export function clearImpersonatedAgency() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event("admin-impersonation-change"));
  } catch (err) {
    console.error("Failed to clear impersonated agency:", err);
  }
}

export function getImpersonatedAgency(): ImpersonatedAgency | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "string" && typeof parsed.name === "string") {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export const IMPERSONATION_EVENT = "admin-impersonation-change";

/**
 * Returns the effective agency ID for the current request:
 * - If an admin is impersonating an agency, returns the impersonated agency ID
 * - Otherwise returns the user's own agency_id from their profile
 */
export function getEffectiveAgencyId(profileAgencyId: string | null | undefined): string | null {
  const impersonated = getImpersonatedAgency();
  return impersonated?.id || profileAgencyId || null;
}
